/** Shared date formatting utilities for consistent display across the app. */

/** Format a date as relative time from now (e.g., "3分钟前", "2天前"). */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "刚刚";
  if (diffMins < 60) return `${diffMins}分钟前`;
  if (diffHours < 24) return `${diffHours}小时前`;
  if (diffDays === 1) return "昨天";
  if (diffDays < 7) return `${diffDays}天前`;
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString("zh-CN", { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("zh-CN", { year: "numeric", month: "short", day: "numeric" });
}

/** Format a date as short Chinese date (e.g., "3月15日"). */
export function formatShortDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("zh-CN", {
    month: "short",
    day: "numeric",
  });
}

/** Format a date as full Chinese date (e.g., "2026年3月15日"). */
export function formatFullDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Format today's date (or a given date) as "YYYY-MM-DD" in local time — used
 *  for default filenames (e.g. the drafting "save as document" default name). */
export function formatIsoDate(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * 秒数格式化为"X 分 Y 秒"（不足一分钟时只显示秒）——批 4/5 异步法律能力等待
 * 期间的心跳（`session.tool.progress`）与 409 在途信息（`elapsedSeconds`）
 * 共用同一份格式化规则，供 {@link ToolCallStrip} 与会话锁占用提示复用。
 */
export function formatElapsedSeconds(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const remainder = total % 60;
  if (minutes === 0) return `${remainder} 秒`;
  return `${minutes} 分 ${remainder} 秒`;
}

/** Format a date string as a datetime string with year, month, day, hour, minute.
 *  Returns "-" if the input is null/undefined. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
